// src/services/refreshTokenInterceptor.ts
import {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';
import { Store } from 'vuex';
import { TokenService } from './tokenService';

class RefreshTokenInterceptor {
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: any) => void;
  }> = [];

  constructor(
    private api: AxiosInstance,
    private store: Store<any>,
    private router: any
  ) {
    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Response interceptor - handle 401 and refresh token
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & {
          _retry?: boolean;
        };

        // Skip refresh for auth endpoints
        if (this.shouldSkipRefresh(originalRequest)) {
          return Promise.reject(error);
        }

        // Handle 401 Unauthorized
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            // Queue this request - ISPRAVKA OVDE
            return new Promise<AxiosResponse>((resolve, reject) => {
              this.failedQueue.push({
                resolve: (token: string) => {
                  if (originalRequest.headers) {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                  }
                  resolve(this.api(originalRequest));
                },
                reject,
              });
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          return new Promise<AxiosResponse>((resolve, reject) => {
            this.refreshAccessToken()
              .then((newToken: string) => {
                if (originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${newToken}`;
                }
                this.processQueue(null, newToken);
                resolve(this.api(originalRequest));
              })
              .catch((err) => {
                this.processQueue(err, null);
                this.handleRefreshFailure();
                reject(err);
              })
              .finally(() => {
                this.isRefreshing = false;
              });
          });
        }

        return Promise.reject(error);
      }
    );
  }

  private shouldSkipRefresh(config?: InternalAxiosRequestConfig): boolean {
    if (!config?.url) return true;

    const skipUrls = [
      '/api/auth/login',
      '/api/auth/register',
      '/api/auth/refresh',
      '/api/auth/logout',
      '/api/auth/csrf-token',
    ];

    return skipUrls.some((url) => config.url?.includes(url));
  }

  private async refreshAccessToken(): Promise<string> {
    const refreshToken = TokenService.getRefreshToken();
    const deviceId = TokenService.getDeviceId();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      // Create new axios instance to avoid loops
      const refreshApi = this.api.create();
      refreshApi.interceptors.response.clear();

      const response = await refreshApi.post('/api/auth/refresh', {
        refreshToken,
        deviceId,
      });

      if (response.data?.status === 'success' && response.data?.data) {
        const { accessToken, refreshToken: newRefreshToken } =
          response.data.data;

        TokenService.setAccessToken(accessToken);
        if (newRefreshToken) {
          TokenService.setRefreshToken(newRefreshToken);
        }

        this.store.commit('auth/SET_TOKEN', accessToken);

        console.log('[RefreshTokenInterceptor] Token refreshed successfully');
        return accessToken;
      } else {
        throw new Error('Invalid refresh response');
      }
    } catch (error: any) {
      console.error('[RefreshTokenInterceptor] Refresh failed:', error);
      throw error;
    }
  }

  private processQueue(error: any, token: string | null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else if (token) {
        resolve(token);
      }
    });
    this.failedQueue = [];
  }

  private handleRefreshFailure() {
    console.log('[RefreshTokenInterceptor] Refresh failed - logging out');

    TokenService.clearAll();
    this.store.dispatch('auth/logout');

    const currentPath = this.router.currentRoute.path;
    if (currentPath !== '/login') {
      localStorage.setItem('redirectAfterLogin', currentPath);
    }

    this.router.push('/login');
  }
}

export default RefreshTokenInterceptor;
